import { useEffect, useMemo, useState } from 'react';
import { Close } from '@mui/icons-material';
import { Box, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { slice } from 'lodash';
import dayjs from 'dayjs';
import { Plus } from 'react-feather';

// Components
import { renderNumberOrDefault, renderTextOrDefault } from 'src/utils/helpers/stringHelpers';
import { FORMAT_DATE_TIME, LIMIT_GET_LIST } from 'src/utils/constants/config';
import Table from 'src/components/Table';
import LineChart from 'src/components/HighChart/LineChart';

// Utilities
import { IFreightRate, IFreightRateIndex } from 'src/utils/typings/response';
import { useQueryDataFreightRateIndexChart } from 'src/services/queries';
import { getArrayDefault } from 'src/utils/helpers/arrayHelpers';
import IconFeather from 'src/components/common/Icon';

interface IProps {
  freightRateSelect: IFreightRate | null;
}

const FreightRateChart = ({ freightRateSelect }: IProps) => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(LIMIT_GET_LIST);
  const [showTableFreightRate, setShowFreightRate] = useState(false);
  const [listFreightRateIndex, setListFreightRateIndex] = useState<Array<IFreightRateIndex>>([]);
  const { data, isFetching } = useQueryDataFreightRateIndexChart({
    locode: freightRateSelect?.locode_origin || '',
    locodeDestination: freightRateSelect?.locode_destination || '',
  });
  const allDataFreightRate = getArrayDefault(data?.data);

  useEffect(() => {
    const initialList = slice(getArrayDefault(allDataFreightRate), page * pageSize, (page + 1) * pageSize);
    setListFreightRateIndex(initialList);
  }, [allDataFreightRate, page]);

  const lineChartYData = useMemo(() => {
    return allDataFreightRate.map((item: IFreightRateIndex) => [item.freight_rate || 0]);
  }, [allDataFreightRate]);

  const lineChartXData = useMemo(() => {
    const res = allDataFreightRate.map((item: IFreightRateIndex) => {
      return dayjs(item?.date, FORMAT_DATE_TIME.yyyy_mm_dd).format(FORMAT_DATE_TIME.FULL_ONLY_DATE);
    });
    return res;
  }, [allDataFreightRate]);

  const configColumn: ConfigCell<IFreightRateIndex>[] = [
    {
      id: 'date',
      disablePadding: true,
      disableSort: true,
      label: 'Date',
      cell: (row: IFreightRateIndex) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderTextOrDefault(row?.date)}
          </Typography>
        );
      },
    },
    {
      id: 'freight_rate',
      disablePadding: true,
      disableSort: true,
      label: `${renderTextOrDefault(freightRateSelect?.locode_origin)} > ${renderTextOrDefault(freightRateSelect?.locode_destination)}`,
      cell: (row: IFreightRateIndex) => {
        return (
          <Typography variant="body2" color="text.secondary">
            {renderNumberOrDefault(row?.freight_rate)}
          </Typography>
        );
      },
    },
  ];

  return (
    <Box p={2} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: theme.shape.borderRadius / 2 }}>
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h5" align="left">
          {renderTextOrDefault(freightRateSelect?.locode_origin)} &gt;{' '}
          {renderTextOrDefault(freightRateSelect?.locode_destination)} (20ft container cost in USD)
        </Typography>
        <IconButton onClick={() => setShowFreightRate(!showTableFreightRate)} color="primary">
          {showTableFreightRate ? <Close /> : <IconFeather icon={<Plus />} />}
        </IconButton>
      </Stack>
      <Box mt={3} />
      {showTableFreightRate ? (
        <Table
          data={listFreightRateIndex}
          configColumn={configColumn}
          dataKey="date"
          onChangePage={(e) => setPage(e)}
          onChangePageSize={(e) => setPageSize(e)}
          page={page}
          pageSize={pageSize}
          loading={isFetching}
          maxRow={5}
          totalRecords={getArrayDefault(allDataFreightRate).length}
          sxTableContainer={{
            border: `1px solid ${theme.palette.divider}`,
          }}
        />
      ) : (
        <LineChart
          loading={isFetching}
          data={lineChartYData}
          dataPropertyKeys={['y', 'custom.number_of_suppliers', 'custom.number_of_buyers']}
          tooltipFormatter={function (this) {
            return `<b>${this.x}</b><br>
<span style="color: ${theme.palette.text.secondary}">${renderTextOrDefault(freightRateSelect?.locode_origin)} > 
    ${renderTextOrDefault(freightRateSelect?.locode_destination)}: <b style="color: white">${renderNumberOrDefault(this.y)}</b></span><br>`;
          }}
          xAxis={{
            allowDecimals: false,
            labels: {
              formatter: function () {
                const year = String(this.value).split('/')[2]; // Extract year from categories
                return year;
              },
            },
            tickInterval: 52, // Show one tick per year (12 months per year or 52 weeks per year)
            categories: lineChartXData,
          }}
        />
      )}
    </Box>
  );
};

export default FreightRateChart;
